import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';

import bluebird from 'utils/bluebird';
import { trimSearch } from '@xmatters/common/utils/searchUtil';
import { getListingURIGenerator } from '@xmatters/redux/actions/listPage/utils';

const uri = '/webapi/xm/1/groups';

export const GROUP_ADDED_SUCCESS = 'GROUP_ADDED_SUCCESS';
export const SEARCH_GROUPS_LIST_PAGE = 'SEARCH_GROUP';
export const SEARCH_GROUPS_LIST_PAGE_START = `${SEARCH_GROUPS_LIST_PAGE}_START`;
export const SEARCH_GROUPS_LIST_PAGE_SUCCESS = `${SEARCH_GROUPS_LIST_PAGE}_SUCCESS`;
export const SEARCH_GROUPS_LIST_PAGE_FAIL = `${SEARCH_GROUPS_LIST_PAGE}_FAIL`;

const getGroupMemberValue = ([member]) => {
  if (member === 'ALL_SHIFTS' || member === 'ANY_SHIFTS') {
    return {
      memberExists: member,
    };
  }
  return {
    memberName: member,
  };
};

/**
 * Get all the filters selections in an object in format availabe to pass to xm-api
 * @param {object} props
 * @param {string} props.searchText
 * @param {object} props.members object containing an array with memberId selection
 * @param {object} props.supervisors object containing an array with selected supervisors
 * @returns {object} returns an object will all filter selections
 */
export const getSearchFilterParams = props => {
  const search = get(props, 'search');
  const status = get(props, 'status.value');
  const supervisors = get(props, 'supervisors.value');
  const members = get(props, 'members.value');
  const dateCreated = get(props, 'dateCreated.value');
  const groupType = get(props, 'groupType.value');

  const params = {};

  if (search) {
    params.search = search;
    params.operand = 'AND';
    params.fields = ['NAME', 'DESCRIPTION'];
  }

  if (status && status.length === 1) {
    params.status = status;
  }

  if (supervisors) {
    params.supervisors = supervisors;
  }

  if (members && members.length === 1) {
    const { memberName, memberExists } = getGroupMemberValue(members);
    if (memberName === 'STAKEHOLDER_USER') {
      params['members.licenseType'] = memberName;
    } else {
      params.members = memberName;
    }
    params['member.exists'] = memberExists;
  }

  if (!isEmpty(dateCreated)) {
    const [
      {
        isDateRange,
        dateRangeFilterFrom,
        dateRangeFilterTo,
        daysToSubtract,
        loggedInUserTimezone,
      },
    ] = dateCreated;

    if (isDateRange) {
      params.createdFrom = moment(dateRangeFilterFrom)
        .tz(loggedInUserTimezone)
        .startOf('day')
        .toISOString();

      params.createdTo = moment(dateRangeFilterTo)
        .tz(loggedInUserTimezone)
        .endOf('day')
        .toISOString();
    } else {
      const fromDate = moment()
        .tz(loggedInUserTimezone)
        .subtract(daysToSubtract, 'days');

      // COR-32011
      // If `daysToSubtract` is 1 (ie: 24 hours), AC 2.b requires us to
      // search 24hrs exactly, hours/mins/secs inclusive. If greater
      // than 1, we'll round the start time down to begin at midnight
      // on the nth day, hence `.startOf('day')`.
      params.createdFrom =
        daysToSubtract === 1
          ? fromDate.toISOString()
          : fromDate.startOf('day').toISOString();
    }
  }

  if (groupType && groupType.length) {
    params.groupType = groupType;
  }

  return params;
};

/**
 * Fetch groups  based on filter and sorting selections
 * @param {object} props
 * @param {string} props.searchText
 * @param {string} props.sortBy: ASCENDING, DESCENDING
 * @param {number} props.startIndex
 * @param {number} props.stopIndex
 * @param {object} props.status: object containing an array with status selection: ACTIVE,DISABLED,ALL
 * @param {object} props.members object containing an array with memberId selection
 * @param {object} props.supervisors object containing an array with selected supervisors
 * @returns {object} a Redux action
 */
export const searchGroups = (props = {}) => {
  const { startIndex = 0, stopIndex = 0, sortBy, sortOrder } = props;

  const params = {
    limit: Math.max(100, stopIndex - startIndex),
    offset: startIndex,
    embed: 'permittedActions,shifts',
    include: 'memberCount, metrics.members',
    ...(sortOrder && { sortOrder: sortOrder.toUpperCase() }),
    ...(sortBy && { sortBy: sortBy.toUpperCase() }),
    ...getSearchFilterParams(props),
  };

  return {
    type: SEARCH_GROUPS_LIST_PAGE,
    meta: {
      xmApi: true,
      method: 'get',
      uri,
      params,
      customUriWithParamsGenerator: getListingURIGenerator,
    },
  };
};

export const GROUPS_LIST_PAGE_HYDRATE_ACTIVE_USERS =
  'GROUPS_LIST_PAGE_HYDRATE_ACTIVE_USERS';
export const GROUPS_LIST_PAGE_HYDRATE_ACTIVE_USERS_START = `${GROUPS_LIST_PAGE_HYDRATE_ACTIVE_USERS}_START`;
export const GROUPS_LIST_PAGE_HYDRATE_ACTIVE_USERS_SUCCESS = `${GROUPS_LIST_PAGE_HYDRATE_ACTIVE_USERS}_SUCCESS`;
export const GROUPS_LIST_PAGE_HYDRATE_ACTIVE_USERS_FAIL = `${GROUPS_LIST_PAGE_HYDRATE_ACTIVE_USERS}_FAIL`;

/**
 * To fetch the list of all the selected supervisors
 * @param {array} activeUserIds
 * @returns {object} a Redux action
 */
export const hydrateActiveUsers = activeUserIds => {
  const groupsUri = '/webapi/xm/1/people';
  const params = {};

  if (activeUserIds) {
    params.id = activeUserIds;
  }

  return {
    type: GROUPS_LIST_PAGE_HYDRATE_ACTIVE_USERS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: groupsUri,
      params,
    },
  };
};

export const GROUPS_LIST_PAGE_SEARCH_USERS = 'GROUPS_LIST_PAGE_SEARCH_USERS';
export const GROUPS_LIST_PAGE_SEARCH_USERS_START = `${GROUPS_LIST_PAGE_SEARCH_USERS}_START`;
export const GROUPS_LIST_PAGE_SEARCH_USERS_SUCCESS = `${GROUPS_LIST_PAGE_SEARCH_USERS}_SUCCESS`;
export const GROUPS_LIST_PAGE_SEARCH_USERS_FAIL = `${GROUPS_LIST_PAGE_SEARCH_USERS}_FAIL`;

/**
 * Fetch users which match the search string
 * @param {string} search
 * @returns {object} a Redux action
 */
export const getUsersBySearch = search => {
  const usersUri = '/webapi/xm/1/people';
  const params = {};

  const trimmedSearchText = trimSearch(search);
  if (trimmedSearchText.length >= 2) {
    params.search = trimmedSearchText;
    params.operand = 'AND';
    params.fields = ['FIRST_NAME', 'LAST_NAME', 'TARGET_NAME'];
  }

  return {
    type: GROUPS_LIST_PAGE_SEARCH_USERS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: usersUri,
      params,
    },
  };
};

export const GROUPS_LIST_PAGE_UPDATE_GROUP_FILTER =
  'GROUPS_LIST_PAGE_UPDATE_GROUP_FILTER';
export const updateGroupFilter = groupFilter => ({
  type: GROUPS_LIST_PAGE_UPDATE_GROUP_FILTER,
  payload: groupFilter,
});

export const GROUPS_LIST_PAGE_RESET_USER_FILTERS =
  'GROUPS_LIST_PAGE_RESET_USERS_FILTERS';
export const GROUPS_LIST_PAGE_ACTIVE_USERS_FILTERS =
  'GROUPS_LIST_PAGE_ACTIVE_USERS_FILTERS';
export const setActiveUserFilters = users => ({
  type: GROUPS_LIST_PAGE_ACTIVE_USERS_FILTERS,
  payload: users,
});
export const resetUserFilters = users => ({
  type: GROUPS_LIST_PAGE_RESET_USER_FILTERS,
  payload: users,
});

export const GROUPS_LIST_PAGE_SET_LOADING = 'GROUPS_LIST_PAGE_SET_LOADING';
export const setIsLoading = () => ({ type: GROUPS_LIST_PAGE_SET_LOADING });

/**
 * Delete single group
 * @param {string} groupUuid : Can also pass the group target name in place of UUID
 * @returns {object} a Redux action
 */
export const deleteGroup = groupUuid => ({
  meta: {
    xmApi: true,
    method: 'del',
    uri: `${uri}/${groupUuid}`,
  },
});

export const GROUPS_LIST_PAGE_DELETE_GROUPS = 'GROUPS_LIST_PAGE_DELETE_GROUPS';
export const GROUPS_LIST_PAGE_DELETE_GROUPS_START = `${GROUPS_LIST_PAGE_DELETE_GROUPS}_START`;
export const GROUPS_LIST_PAGE_DELETE_GROUPS_SUCCESS = `${GROUPS_LIST_PAGE_DELETE_GROUPS}_SUCCESS`;
export const GROUPS_LIST_PAGE_DELETE_GROUPS_FAIL = `${GROUPS_LIST_PAGE_DELETE_GROUPS}_FAIL`;

/**
 * To delete all the selected groups
 * @param {array} groupUuids
 * @returns {function}
 */
export const deleteGroups = groupUuids => dispatch => {
  const onSuccess = () =>
    dispatch({ type: GROUPS_LIST_PAGE_DELETE_GROUPS_SUCCESS });
  const onFail = e => {
    dispatch({ type: GROUPS_LIST_PAGE_DELETE_GROUPS_FAIL });
    throw e;
  };

  dispatch({ type: GROUPS_LIST_PAGE_DELETE_GROUPS_START });
  return bluebird.mapSeries(groupUuids, uuid =>
    dispatch(deleteGroup(uuid)).then(onSuccess).catch(onFail),
  );
};

export const GROUPS_LIST_PAGE_EXPORT_GROUPS = 'GROUPS_LIST_PAGE_EXPORT_GROUPS';
export const GROUPS_LIST_PAGE_EXPORT_GROUPS_START = `${GROUPS_LIST_PAGE_EXPORT_GROUPS}_START`;
export const GROUPS_LIST_PAGE_EXPORT_GROUPS_FINISHED = `${GROUPS_LIST_PAGE_EXPORT_GROUPS}_FINISHED`;
export const exportGroups = () => {
  return {
    type: GROUPS_LIST_PAGE_EXPORT_GROUPS,
  };
};

export const exportGroupsFinished = () => ({
  type: GROUPS_LIST_PAGE_EXPORT_GROUPS_FINISHED,
});

export const GROUPS_LIST_PAGE_GET_GROUP_SHIFTS =
  'GROUPS_LIST_PAGE_GET_GROUP_SHIFTS';
export const GROUPS_LIST_PAGE_GET_GROUP_SHIFTS_START = `${GROUPS_LIST_PAGE_GET_GROUP_SHIFTS}_START`;
export const GROUPS_LIST_PAGE_GET_GROUP_SHIFTS_SUCCESS = `${GROUPS_LIST_PAGE_GET_GROUP_SHIFTS}_SUCCESS`;
export const GROUPS_LIST_PAGE_GET_GROUP_SHIFTS_FAIL = `${GROUPS_LIST_PAGE_GET_GROUP_SHIFTS}_FAIL`;

/**
 * Fetch all shifts of a specified group
 * @param {string} groupId
 * @returns {object} a Redux action
 */
export const getShiftsByGroupId = groupId => {
  const shiftsUri = `/webapi/xm/1/groups/${groupId}/shifts`;
  const params = {
    shiftStatus: 'SCHEDULED',
    include: 'memberCount',
    limit: 1000,
  };

  return {
    type: GROUPS_LIST_PAGE_GET_GROUP_SHIFTS,
    meta: {
      xmApi: true,
      method: 'get',
      uri: shiftsUri,
      params,
    },
  };
};
